import { withSubmitGuard } from '../submitGuard.js';
import { esc } from '../escape.js';

// Timesheets / time-clock / payroll now live in their own top-level panel
// (Javascript/Views/timesheetView.js) - this view is Members + Roles only.

// Role icon set (picked in the role editor; stored as roles.icon). Each key
// pairs an SVG with a fixed accent color so roles look distinct at a glance.
// Legacy roles without an icon fall back to one derived from the name hash.
export const ROLE_ICONS = {
  shield:    { color: '#22c55e', svg: `<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>` },
  star:      { color: '#f59e0b', svg: `<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>` },
  utensils:  { color: '#ef4444', svg: `<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>` },
  coffee:    { color: '#b45309', svg: `<path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" y1="2" x2="6" y2="4"/><line x1="10" y1="2" x2="10" y2="4"/><line x1="14" y1="2" x2="14" y2="4"/>` },
  card:      { color: '#3b82f6', svg: `<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>` },
  clipboard: { color: '#8b5cf6', svg: `<rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>` },
  headset:   { color: '#06b6d4', svg: `<path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm18 0h-3a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-5Z"/><path d="M21 11a9 9 0 0 0-18 0"/>` },
  sparkles:  { color: '#ec4899', svg: `<path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"/>` },
};
const ROLE_ICON_KEYS = Object.keys(ROLE_ICONS);

export const roleIconKeyFor = (role) => {
  if (role?.icon && ROLE_ICONS[role.icon]) return role.icon;
  if (role?.locked) return 'shield';
  let hash = 0;
  for (const ch of role?.name ?? '') hash = (hash * 31 + ch.charCodeAt(0)) & 0xffffffff;
  return ROLE_ICON_KEYS[Math.abs(hash) % ROLE_ICON_KEYS.length];
};

export const roleIconFor = (role) => ROLE_ICONS[roleIconKeyFor(role)];

const roleIconTile = (role, size = 40) => {
  const { color, svg } = roleIconFor(role);
  return `
    <span class="staff-role-tile" style="background:color-mix(in srgb, ${color} 11%, transparent);color:${color};width:${size}px;height:${size}px">
      <svg xmlns="http://www.w3.org/2000/svg" width="${Math.round(size * 0.48)}" height="${Math.round(size * 0.48)}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${svg}</svg>
    </span>`;
};

class StaffView {
  _panel      = document.querySelector('#staffPanel');
  _list       = document.querySelector('#staffList');
  _rolesEl    = document.querySelector('#staffRolesContent');
  _formModal  = document.querySelector('#inviteStaffModal');

  open(canManage = false) {
    this._panel.classList.remove('hidden', 'cashflow-exiting');
    const inviteBtn = this._panel.querySelector('#inviteStaffBtn');
    if (inviteBtn) inviteBtn.classList.toggle('hidden', !canManage);
    this._switchTab('members');
  }

  _switchTab(tab) {
    this._panel.querySelectorAll('.staff-tab').forEach(btn => {
      btn.classList.toggle('staff-tab--active', btn.dataset.tab === tab);
    });
    document.getElementById('staffMembersWrap')?.classList.toggle('hidden', tab !== 'members');
    this._rolesEl.classList.toggle('hidden', tab !== 'roles');
  }

  _addHandlerTabs() {
    this._panel.querySelector('.staff-sidebar')?.addEventListener('click', (e) => {
      const btn = e.target.closest('.staff-tab, #inviteStaffBtn');
      if (!btn) return;
      if (btn.classList.contains('staff-tab')) this._switchTab(btn.dataset.tab);
      this._closeDrawer(); // Invite opens a modal; either way the drawer's job is done
    });
  }

  // ── Mobile sidebar drawer ────────────────────────────────────────────────────
  // Below 640px the sidebar is an off-canvas drawer (same pattern as the
  // Settings modal's mobile nav): opened from the header hamburger, closed by
  // the backdrop or by picking a tab / Invite.

  _openDrawer() {
    this._panel.querySelector('#staffSidebar')?.classList.add('panel-sidebar--open');
    this._panel.querySelector('#staffNavBackdrop')?.classList.add('panel-nav-backdrop--visible');
    this._panel.querySelector('#staffNavBtn')?.setAttribute('aria-expanded', 'true');
  }

  _closeDrawer() {
    this._panel.querySelector('#staffSidebar')?.classList.remove('panel-sidebar--open');
    this._panel.querySelector('#staffNavBackdrop')?.classList.remove('panel-nav-backdrop--visible');
    this._panel.querySelector('#staffNavBtn')?.setAttribute('aria-expanded', 'false');
  }

  _addHandlerDrawer() {
    const btn = this._panel.querySelector('#staffNavBtn');
    btn?.addEventListener('click', () => {
      if (btn.getAttribute('aria-expanded') === 'true') this._closeDrawer();
      else this._openDrawer();
    });
    this._panel.querySelector('#staffNavBackdrop')?.addEventListener('click', () => this._closeDrawer());
  }

  close() {
    this._closeDrawer();
    this._panel.classList.add('cashflow-exiting');
    setTimeout(() => {
      this._panel.classList.add('hidden');
      this._panel.classList.remove('cashflow-exiting');
    }, 220);
  }

  _staffCache = { staff: [], canManage: false, roles: [] };
  // Set by the controller from the reset_staff_mfa capability; gates the
  // "Remove 2FA" menu item separately from the rest of the manage_staff actions.
  canResetMFA = false;
  _staffQuery = '';
  _moreWired  = false;

  render(staff, canManage = false, roles = []) {
    this._staffCache = { staff, canManage, roles };
    if (!this._moreWired) {
      this._moreWired = true;
      // "More" dropdown: toggle its own menu, close any other; outside click closes all.
      this._list.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action="more"]');
        if (!btn) return;
        const menu = btn.closest('.staff-more')?.querySelector('.staff-more-menu');
        this._list.querySelectorAll('.staff-more-menu').forEach(m => { if (m !== menu) m.classList.add('hidden'); });
        menu?.classList.toggle('hidden');
      });
      document.addEventListener('click', (e) => {
        if (e.target.closest('.staff-more')) return;
        this._list.querySelectorAll('.staff-more-menu').forEach(m => m.classList.add('hidden'));
      });
    }
    const head = document.getElementById('staffMembersHead');
    if (head) {
      head.innerHTML = `
        <div class="staff-members-top">
          <div>
            <h3 class="adj-section-title">Staff</h3>
            <p class="adj-section-hint">Manage your team members and their access.</p>
          </div>
          <div class="staff-members-tools">
            <div class="staff-search">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" id="staffSearchInput" placeholder="Search members..." autocomplete="off" value="${esc(this._staffQuery)}" />
            </div>
            <span class="staff-members-count">${staff.length} member${staff.length === 1 ? '' : 's'}</span>
          </div>
        </div>
        <div class="staff-table-cols">
          <span>Member</span><span>Role</span><span>Status</span><span>PIN Access</span><span class="staff-col-actions">Actions</span>
        </div>`;
      const input = document.getElementById('staffSearchInput');
      input?.addEventListener('input', () => {
        this._staffQuery = input.value;
        this._renderListBody();
      });
    }
    this._renderListBody();
  }

  _renderListBody() {
    const { staff, canManage, roles } = this._staffCache;
    const q = this._staffQuery.trim().toLowerCase();
    const filtered = q
      ? staff.filter(s => `${s.firstName ?? ''} ${s.lastName ?? ''} ${s.email ?? ''}`.toLowerCase().includes(q))
      : staff;
    this._list.innerHTML = q && !filtered.length
      ? `<li class="staff-empty">No members match "${esc(this._staffQuery.trim())}".</li>`
      : this._generateListMarkup(filtered, canManage, roles);
  }

  // opts = { canManage, ownRoleId, labels, totalCaps, counts } (counts = roleId -> member count)
  renderRoles(roles, opts = {}) {
    const { canManage = false, ownRoleId = null, labels = {}, counts = {} } = opts;
    if (!roles?.length) {
      this._rolesEl.innerHTML = `<p class="staff-empty">No roles defined.</p>`;
      return;
    }
    const cards = roles.map((r) => {
      const isOwnRole = r.id === ownRoleId;
      const chips = r.locked
        ? ''
        : (r.caps?.length
            ? r.caps.slice(0, 4).map((k) => `<span class="staff-role-chip">${esc(labels[k] ?? k)}</span>`).join('')
              + (r.caps.length > 4 ? `<span class="staff-role-chip staff-role-chip--more">+${r.caps.length - 4}</span>` : '')
            : `<span class="staff-role-chip staff-role-chip--none">No access</span>`);
      const desc = r.locked
        ? 'Full access to all features and settings.'
        : (r.description || `${r.caps?.length ?? 0} capabilities granted.`);
      const nMembers = counts[r.id] ?? 0;

      let actions = '';
      if (canManage) {
        if (r.locked) {
          actions = `<span class="staff-role-locked" title="The Admin role always has full access">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Locked
          </span>`;
        } else if (isOwnRole) {
          actions = `<span class="staff-role-locked" title="You can't edit the role you're currently assigned to">Your role</span>`;
        } else {
          actions = `
            <button class="staff-action-btn" data-role-action="edit" data-id="${r.id}" type="button" title="Edit role">Edit</button>
            <button class="staff-action-btn staff-action-btn--remove" data-role-action="delete" data-id="${r.id}" type="button" title="Delete role">Delete</button>`;
        }
      }

      return `
        <div class="staff-role-card">
          ${roleIconTile(r, 44)}
          <div class="staff-role-card-info">
            <div class="staff-role-card-name">
              ${esc(r.name)}
              ${r.locked ? `<span class="staff-role-system">System role</span>` : ''}
            </div>
            <div class="staff-role-card-desc">${esc(desc)}</div>
            ${chips ? `<div class="staff-role-chips">${chips}</div>` : ''}
          </div>
          <span class="staff-role-members">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            ${nMembers} member${nMembers === 1 ? '' : 's'}
          </span>
          <div class="staff-role-card-actions">${actions}</div>
        </div>`;
    }).join('');

    this._rolesEl.innerHTML = `
      <div class="staff-members-top">
        <div>
          <h3 class="adj-section-title">Roles</h3>
          <p class="adj-section-hint">Create and manage roles with specific permissions.</p>
        </div>
        ${canManage ? `<button class="btn primary" id="staffAddRoleBtn" type="button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
          New Role
        </button>` : ''}
      </div>
      <div class="staff-role-list">${cards}</div>`;
  }

  _generateListMarkup(staff, canManage, roles = []) {
    if (!staff.length)
      return `<li class="staff-empty">No staff members yet. Hit "+ Invite" to add someone.</li>`;

    const AVATAR_COLORS = ['#22c55e','#3b82f6','#f59e0b','#ec4899','#8b5cf6','#06b6d4'];
    const ROLE_CHIP_COLORS = ['#3b82f6','#f59e0b','#8b5cf6','#06b6d4','#ec4899','#ef4444'];

    return staff.map((s, i) => {
      const initials = `${s.firstName?.[0] ?? ''}${s.lastName?.[0] ?? ''}`.toUpperCase() || '?';
      const color    = AVATAR_COLORS[i % AVATAR_COLORS.length];
      const role     = roles.find(r => r.id === s.roleId);
      const isAdmin  = role?.locked || s.role === 'Admin';
      const chipColor = isAdmin ? 'var(--brand-1)' : ROLE_CHIP_COLORS[Math.max(0, roles.findIndex(r => r.id === s.roleId)) % ROLE_CHIP_COLORS.length];
      const roleSub  = isAdmin ? 'Full access' : (role?.description || 'Custom role');
      return `
      <li class="staff-item" data-id="${s.id}">
        <div class="staff-cell staff-cell--who">
          <div class="staff-avatar" style="background:${color}">${esc(initials)}</div>
          <div class="staff-item-info">
            <div class="staff-item-name">${esc(s.firstName)} ${esc(s.lastName)}${s.isSelf ? ' <span class="staff-you-tag">You</span>' : ''}</div>
            <div class="staff-item-email">${esc(s.email)}</div>
          </div>
        </div>
        <div class="staff-cell staff-cell--role">
          <span class="staff-role-badge" style="background:color-mix(in srgb, ${chipColor} 12%, transparent);color:${chipColor}">${esc(s.role)}</span>
          <span class="staff-cell-sub">${esc(roleSub)}</span>
        </div>
        <div class="staff-cell">
          <span class="staff-status-badge staff-status-badge--${s.isPending ? 'pending' : 'active'}">
            <span class="staff-status-dot"></span>${s.isPending ? 'Pending' : 'Active'}
          </span>
        </div>
        <div class="staff-cell staff-cell--pin">
          <span class="staff-pin-lock${s.hasPin ? ' staff-pin-lock--set' : ''}">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </span>
          <div class="staff-item-info">
            <span class="staff-pin-label">${s.hasPin ? 'PIN set' : 'No PIN'}</span>
            <span class="staff-cell-sub">${s.hasPin ? '••••' : 'Not set'}</span>
          </div>
        </div>
        <div class="staff-cell staff-cell--actions">
        ${canManage && !s.isSelf ? `
          <div class="staff-more">
            <button class="staff-action-btn staff-more-btn staff-more-btn--lg" data-action="more" type="button" aria-label="More actions">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
              More
            </button>
            <div class="staff-more-menu hidden">
              ${!s.isOwner ? `
              <button class="staff-more-item" data-id="${s.id}" data-action="edit" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </button>` : ''}
              <button class="staff-more-item" data-id="${s.id}" data-action="pin" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                ${s.hasPin ? 'Change PIN' : 'Set PIN'}
              </button>
              ${!s.isOwner && this.canResetMFA ? `
              <button class="staff-more-item" data-id="${s.id}" data-action="remove-mfa" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="9.5" y1="9.5" x2="14.5" y2="14.5"/><line x1="14.5" y1="9.5" x2="9.5" y2="14.5"/></svg>
                Remove 2FA
              </button>` : ''}
              ${!s.isOwner ? `
              <button class="staff-more-item staff-more-item--danger" data-id="${s.id}" data-action="remove" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                Remove member
              </button>` : ''}
            </div>
          </div>
        ` : ''}
        </div>
      </li>`;
    }).join('');
  }

  showInviteForm(roles) {
    const formEl = this._formModal.querySelector('#inviteStaffForm');
    formEl.innerHTML = `
      <button class="modal-close-btn" id="inviteFormCloseBtn" type="button">&times;</button>
      <h2 class="edit-form-title">Invite Staff Member</h2>

      <div class="edit-fields-row">
        <div class="edit-field">
          <label for="inviteFirstName">First name</label>
          <input type="text" id="inviteFirstName" placeholder="Jane" autocomplete="off" />
        </div>
        <div class="edit-field">
          <label for="inviteLastName">Last name</label>
          <input type="text" id="inviteLastName" placeholder="Smith" autocomplete="off" />
        </div>
      </div>

      <div class="edit-field">
        <label for="inviteEmail">Email</label>
        <input type="email" id="inviteEmail" placeholder="staff@example.com" autocomplete="off" />
      </div>

      <div class="edit-field">
        <label for="inviteRole">Role</label>
        <select id="inviteRole" class="settings-select">
          ${roles.map(r => `<option value="${esc(r.id)}">${esc(r.name)}</option>`).join('')}
        </select>
      </div>

      <div id="inviteTcReflect">${this._tcReflectHtml(roles[0])}</div>

      <p class="settings-section-hint" style="margin-top:8px;">
        An invite email will be sent to this address. They'll set their password and PIN when they click the link.
      </p>

      <div class="adj-form-actions">
        <button type="button" class="btn" id="inviteCancelBtn">Cancel</button>
        <button type="button" class="btn primary" id="inviteSaveBtn">Add Member</button>
      </div>
    `;

    document.getElementById('inviteFormCloseBtn')?.addEventListener('click', () => this.closeForm());
    document.getElementById('inviteCancelBtn')?.addEventListener('click', () => this.closeForm());

    const roleSel = document.getElementById('inviteRole');
    const reflectEl = document.getElementById('inviteTcReflect');
    roleSel?.addEventListener('change', () => {
      if (reflectEl) reflectEl.innerHTML = this._tcReflectHtml(roles.find(r => r.id === roleSel.value));
    });

    this._formModal.classList.remove('hidden');
    document.getElementById('inviteFirstName')?.focus();
  }

  // Read-only reflection of whether a role grants time-clock access. Access is
  // role-based (the use_timeclock capability), so this just mirrors it - the
  // owner changes it under Staff → Roles, keeping a single source of truth.
  _tcReflectHtml(role) {
    const on = Array.isArray(role?.caps) && role.caps.includes('use_timeclock');
    return `
      <div class="tc-reflect tc-reflect--${on ? 'on' : 'off'}">
        <span class="tc-reflect-dot"></span>
        <div class="tc-reflect-text">
          <p class="tc-reflect-title">Time tracking: <strong>${on ? 'On' : 'Off'}</strong>${role ? ` <span class="tc-reflect-role">(from ${esc(role.name)})</span>` : ''}</p>
          <p class="tc-reflect-hint">${on
            ? 'This role can clock in and out on the Time Clock.'
            : "This role can't use the Time Clock."} Time-clock access is set by the role - edit it under Staff → Roles.</p>
        </div>
      </div>`;
  }

  closeForm() {
    this._formModal.classList.add('hidden');
    this._formModal.querySelector('#inviteStaffForm').innerHTML = '';
  }

  _getInviteData() {
    return {
      firstName: document.getElementById('inviteFirstName')?.value.trim(),
      lastName:  document.getElementById('inviteLastName')?.value.trim(),
      email:     document.getElementById('inviteEmail')?.value.trim(),
      roleId:    document.getElementById('inviteRole')?.value,
    };
  }

  // ── Handlers ──────────────────────────────────────────────────────────────────

  _addHandlerOpen(handler) {
    document.addEventListener('click', (e) => {
      if (!e.target.closest("[data-action='staff']")) return;
      handler();
    });
  }

  _addHandlerClose(handler) {
    this._panel.querySelector('.staff-back')?.addEventListener('click', () => handler());
    this._formModal.addEventListener('click', (e) => {
      if (e.target === this._formModal) this.closeForm();
    });
  }

  _addHandlerInvite(handler) {
    this._panel.querySelector('#inviteStaffBtn')?.addEventListener('click', () => handler());
  }

  _addHandlerSaveInvite(handler) {
    let submitting = false;
    this._formModal.addEventListener('click', async (e) => {
      if (!e.target.closest('#inviteSaveBtn')) return;
      // Guard against rapid double-clicks: each click would otherwise fire another
      // invite before the first insert lands (the duplicate-email check reads stale
      // state.staff), creating multiple pending rows for the same person.
      if (submitting) return;
      const data = this._getInviteData();
      if (!data.firstName) { alert('Please enter a first name.'); return; }
      if (!data.lastName)  { alert('Please enter a last name.'); return; }
      if (!data.email)     { alert('Please enter an email.'); return; }
      submitting = true;
      const btn = document.getElementById('inviteSaveBtn');
      const prevText = btn?.textContent;
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      try {
        await handler(data);
      } finally {
        submitting = false;
        if (btn) { btn.disabled = false; btn.textContent = prevText ?? 'Add Member'; }
      }
    });
  }

  _addHandlerRemove(handler) {
    this._list.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action="remove"]');
      if (!btn) return;
      handler(btn.dataset.id);
    });
  }

  _addHandlerRemoveMFA(handler) {
    this._list.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action="remove-mfa"]');
      if (!btn) return;
      handler(btn.dataset.id);
    });
  }

  // Busy state for the "Remove 2FA" menu item while the request is in flight
  // (an Edge Function round-trip, so worth showing). Stashes the original
  // markup on the button itself so it can be restored exactly once done.
  setRemoveMFALoading(staffId, loading) {
    const btn = this._list.querySelector(`[data-action="remove-mfa"][data-id="${staffId}"]`);
    if (!btn) return;
    if (loading) {
      btn.dataset.origHtml = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span class="btn-spinner"></span>Removing…';
    } else if (btn.dataset.origHtml) {
      btn.disabled = false;
      btn.innerHTML = btn.dataset.origHtml;
      delete btn.dataset.origHtml;
    }
  }

  _addHandlerEditRole(handler) {
    this._list.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action="edit"]');
      if (!btn) return;
      if (document.getElementById('staffEditRoleModal')) return;
      const staffId = btn.dataset.id;
      handler(staffId);
    });
  }

  // Fires for the role-editor entry points in the Roles tab: create / edit / delete.
  _addHandlerRoleAction(handler) {
    this._rolesEl.addEventListener('click', (e) => {
      if (e.target.closest('#staffAddRoleBtn')) { handler('create', null); return; }
      const btn = e.target.closest('[data-role-action]');
      if (!btn) return;
      handler(btn.dataset.roleAction, btn.dataset.id);
    });
  }

  // Capability editor for a role. opts = { role, isNew, allPermissions, labels,
  // descriptions, groups, grantableCaps }. Capabilities are shown grouped, each
  // group with a "check all" header toggle. grantableCaps = what the current user
  // may hand out (can't grant what you don't hold). Returns { onSave(cb) } where cb
  // receives (name, capsArray).
  showRoleEditor({ role, isNew, allPermissions, labels, descriptions = {}, groups, grantableCaps, members = [] }) {
    // New roles start with time-clock access on by default (everyone can clock in).
    const granted = new Set(role?.caps ?? (isNew ? ['use_timeclock'] : []));
    const grantable = new Set(grantableCaps ?? []);

    // Fall back to one big group if none provided.
    const groupList = (groups?.length ? groups : [{ label: '', keys: allPermissions }])
      .map((g) => ({ label: g.label, keys: g.keys.filter((k) => allPermissions.includes(k)) }))
      .filter((g) => g.keys.length);

    const renderRow = (key) => {
      const canGrant = grantable.has(key);
      const checked = granted.has(key) ? 'checked' : '';
      const disabled = canGrant ? '' : 'disabled';
      const hint = canGrant ? '' : `<span class="role-cap-hint">you don't have this</span>`;
      const desc = descriptions[key] ? `<span class="role-cap-desc">${esc(descriptions[key])}</span>` : '';
      return `
        <label class="role-cap-row ${canGrant ? '' : 'role-cap-row--locked'}">
          <input type="checkbox" data-cap="${key}" data-group="${esc(key)}" ${checked} ${disabled} />
          <span class="role-cap-text">
            <span class="role-cap-label">${esc(labels[key] ?? key)} ${hint}</span>
            ${desc}
          </span>
        </label>`;
    };

    const groupsHtml = groupList.map((g, i) => {
      const anyGrantable = g.keys.some((k) => grantable.has(k));
      const headerCb = anyGrantable
        ? `<input type="checkbox" class="role-cap-group-toggle" data-group-idx="${i}" />`
        : `<input type="checkbox" disabled />`;
      const rows = g.keys.map(renderRow).join('');
      return `
        <div class="role-cap-group" data-group-idx="${i}">
          ${g.label ? `<label class="role-cap-group-header">${headerCb}<span>${esc(g.label)}</span></label>` : ''}
          ${rows}
        </div>`;
    }).join('');

    const modal = document.createElement('div');
    modal.className = 'cashier-picker-overlay';
    modal.id = 'staffRoleEditorModal';
    modal.style.zIndex = '99999';
    modal.innerHTML = `
      <div class="cashier-picker-card role-editor-card" style="max-width:460px;width:calc(100vw - 40px)">
        <div class="cashier-picker-header">
          <div>
            <h3 class="cashier-picker-title">${isNew ? 'New role' : 'Edit role'}</h3>
            <p class="cashier-picker-subtitle">Choose what this role can access</p>
          </div>
          <button class="modal-close-btn" id="roleEditorClose" type="button">&times;</button>
        </div>
        <div class="edit-field" style="margin-top:8px">
          <label for="roleEditorName">Role name</label>
          <input type="text" id="roleEditorName" class="settings-input" maxlength="40"
            placeholder="e.g. Shift Lead" value="${esc(role?.name ?? '')}" ${role?.locked ? 'disabled' : ''} />
        </div>
        <div class="edit-field" style="margin-top:10px">
          <label for="roleEditorDesc">Short description <span class="role-optional">(optional)</span></label>
          <input type="text" id="roleEditorDesc" class="settings-input" maxlength="80"
            placeholder="e.g. Manages operations and reports" value="${esc(role?.description ?? '')}" />
        </div>
        <div class="edit-field" style="margin-top:10px">
          <label>Icon</label>
          <div class="role-icon-picker" id="roleIconPicker">
            ${ROLE_ICON_KEYS.map(key => {
              const { color, svg } = ROLE_ICONS[key];
              const selected = (role?.icon ?? (isNew ? '' : roleIconKeyFor(role))) === key;
              return `
                <button type="button" class="role-icon-option${selected ? ' role-icon-option--selected' : ''}"
                  data-icon="${key}" aria-label="${key}"
                  style="background:color-mix(in srgb, ${color} 11%, transparent);color:${color}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${svg}</svg>
                </button>`;
            }).join('')}
          </div>
        </div>
        ${!isNew ? `
        <div class="edit-field" style="margin-top:10px">
          <label>Members with this role${members.length ? ` (${members.length})` : ''}</label>
          ${members.length ? `
          <div class="role-members-list">
            ${members.map((m, i) => {
              const name = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || (m.email ?? '?');
              const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
              const colors = ['#22c55e','#3b82f6','#f59e0b','#ec4899','#8b5cf6','#06b6d4'];
              return `
                <span class="role-member-chip">
                  <span class="role-member-avatar" style="background:${colors[i % colors.length]}">${esc(initials)}</span>
                  ${esc(name)}
                </span>`;
            }).join('')}
          </div>` : `<p class="role-members-none">Nobody has this role yet.</p>`}
        </div>` : ''}
        <div class="role-cap-list">${groupsHtml}</div>
        <div class="adj-form-actions" style="margin-top:16px">
          <button type="button" class="btn" id="roleEditorCancel">Cancel</button>
          <button type="button" class="btn primary" id="roleEditorSave">${isNew ? 'Create role' : 'Save changes'}</button>
        </div>
      </div>`;
    document.body.appendChild(modal);
    const close = () => modal.remove();
    document.getElementById('roleEditorClose')?.addEventListener('click', close);
    document.getElementById('roleEditorCancel')?.addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });

    // Icon picker: single-select toggle
    const picker = modal.querySelector('#roleIconPicker');
    picker?.addEventListener('click', (e) => {
      const opt = e.target.closest('.role-icon-option');
      if (!opt) return;
      picker.querySelectorAll('.role-icon-option').forEach(o =>
        o.classList.toggle('role-icon-option--selected', o === opt));
    });

    // ── Group "check all" sync ──────────────────────────────────────────────────
    const groupEls = [...modal.querySelectorAll('.role-cap-group')];
    const childInputs = (groupEl) => [...groupEl.querySelectorAll('input[data-cap]:not(:disabled)')];

    const syncGroupHeader = (groupEl) => {
      const toggle = groupEl.querySelector('.role-cap-group-toggle');
      if (!toggle) return;
      const kids = childInputs(groupEl);
      const checkedCount = kids.filter((c) => c.checked).length;
      toggle.checked = kids.length > 0 && checkedCount === kids.length;
      toggle.indeterminate = checkedCount > 0 && checkedCount < kids.length;
    };

    groupEls.forEach((groupEl) => {
      syncGroupHeader(groupEl);
      const toggle = groupEl.querySelector('.role-cap-group-toggle');
      toggle?.addEventListener('change', () => {
        childInputs(groupEl).forEach((c) => { c.checked = toggle.checked; });
        toggle.indeterminate = false;
      });
      groupEl.addEventListener('change', (e) => {
        if (e.target.matches('input[data-cap]')) syncGroupHeader(groupEl);
      });
    });

    return {
      onSave: (cb) => {
        const saveBtn = document.getElementById('roleEditorSave');
        saveBtn?.addEventListener('click', () => {
          const name = document.getElementById('roleEditorName')?.value.trim() ?? '';
          const caps = [...modal.querySelectorAll('input[data-cap]:checked')].map((el) => el.dataset.cap);
          const meta = {
            description: document.getElementById('roleEditorDesc')?.value.trim() || null,
            icon:        modal.querySelector('.role-icon-option--selected')?.dataset.icon || null,
          };
          withSubmitGuard(saveBtn, () => cb(name, caps, close, meta), { busyText: 'Saving…' });
        });
      },
    };
  }

  showEditRoleModal(staff, roles) {
    const name = `${staff.firstName ?? ''} ${staff.lastName ?? ''}`.trim();
    const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
    let hash = 0;
    for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) & 0xffffffff;
    const avatarColor = ['#22c55e','#3b82f6','#f59e0b','#ec4899','#8b5cf6','#06b6d4'][Math.abs(hash) % 6];

    const modal = document.createElement('div');
    modal.className = 'cashier-picker-overlay';
    modal.id = 'staffEditRoleModal';
    modal.style.zIndex = '99999';
    modal.innerHTML = `
      <div class="cashier-picker-card" style="max-width:360px;width:calc(100vw - 40px)">
        <div class="cashier-picker-header">
          <div class="edit-staff-who">
            <div class="staff-avatar" style="background:${avatarColor}">${esc(initials)}</div>
            <div>
              <h3 class="cashier-picker-title">Edit Staff</h3>
              <p class="cashier-picker-subtitle">${esc(name)}</p>
              ${staff.email ? `<p class="edit-staff-email">${esc(staff.email)}</p>` : ''}
            </div>
          </div>
          <button class="modal-close-btn" id="editRoleCloseBtn" type="button">&times;</button>
        </div>
        <div class="edit-field" style="margin-top:8px">
          <label for="editRoleSelect">Role</label>
          <select id="editRoleSelect" class="settings-select">
            ${roles.map(r => `<option value="${esc(r.id)}" ${r.id === staff.roleId ? 'selected' : ''}>${esc(r.name)}</option>`).join('')}
          </select>
          <div id="editTcReflect" style="margin-top:8px">${this._tcReflectHtml(roles.find(r => r.id === staff.roleId) ?? roles[0])}</div>
        </div>
        <div class="adj-form-actions" style="margin-top:16px">
          <button type="button" class="btn" id="editRoleCancelBtn">Cancel</button>
          <button type="button" class="btn primary" id="editRoleSaveBtn">Save</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    const close = () => modal.remove();
    document.getElementById('editRoleCloseBtn')?.addEventListener('click', close);
    document.getElementById('editRoleCancelBtn')?.addEventListener('click', close);
    modal.addEventListener('click', e => { if (e.target === modal) close(); });

    const editRoleSel = document.getElementById('editRoleSelect');
    const editReflectEl = document.getElementById('editTcReflect');
    editRoleSel?.addEventListener('change', () => {
      if (editReflectEl) editReflectEl.innerHTML = this._tcReflectHtml(roles.find(r => r.id === editRoleSel.value));
    });
    return {
      onSave: (cb) => {
        document.getElementById('editRoleSaveBtn')?.addEventListener('click', () => {
          const roleId = document.getElementById('editRoleSelect')?.value;
          close();
          cb(roleId);
        });
      }
    };
  }

  _addHandlerSetPin(handler) {
    this._list.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action="pin"]');
      if (!btn) return;
      if (document.querySelector('.cashier-picker-overlay')) return;
      const staffId = btn.dataset.id;
      const modal = document.createElement('div');
      modal.className = 'cashier-picker-overlay';
      modal.style.zIndex = '99999';
      modal.innerHTML = `
        <div class="cashier-picker-card" style="max-width:320px">
          <div class="cashier-picker-header">
            <div>
              <h3 class="cashier-picker-title">Set PIN</h3>
              <p class="cashier-picker-subtitle">6-digit cashier PIN</p>
            </div>
            <button class="modal-close-btn" data-action="pin-close" type="button">&times;</button>
          </div>
          <form class="edit-field" style="margin-top:8px" onsubmit="return false">
            <input type="password" data-input="pin" placeholder="••••••" maxlength="6" inputmode="numeric" autocomplete="new-password" class="settings-input pin-input" />
          </form>
          <div class="adj-form-actions" style="margin-top:16px">
            <button type="button" class="btn" data-action="pin-cancel">Cancel</button>
            <button type="button" class="btn primary" data-action="pin-save">Save PIN</button>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
      const pinInput = modal.querySelector('[data-input="pin"]');
      pinInput?.focus();
      const close = () => modal.remove();
      modal.querySelector('[data-action="pin-close"]')?.addEventListener('click', close);
      modal.querySelector('[data-action="pin-cancel"]')?.addEventListener('click', close);
      modal.addEventListener('click', e => { if (e.target === modal) close(); });
      modal.querySelector('[data-action="pin-save"]')?.addEventListener('click', () => {
        const pin = pinInput?.value.trim();
        if (!pin || !/^\d{6}$/.test(pin)) {
          pinInput?.classList.add('input-error');
          return;
        }
        close();
        handler(staffId, pin);
      });
    });
  }
}

const _view = new StaffView();
_view._addHandlerTabs();
_view._addHandlerDrawer();
export default _view;
